import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  LogOut, Phone, MessageCircle, MapPin, Users, Briefcase, Clock,
  Play, CheckCircle2, RefreshCw, PlaneTakeoff, FileText, ArrowRight,
  Bell, BellOff, X, Wallet, Navigation2, Receipt, TrendingUp, CalendarDays, FolderOpen, UserRound, Gift,
} from "lucide-react";
import { toast } from "sonner";
import driverApi from "@/lib/driverApi";
import InstallPrompt from "@/components/InstallPrompt";
import SettlementModal from "@/components/SettlementModal";
import DriverReceiptUpload from "@/components/DriverReceiptUpload";
import AvailabilityToggle from "@/components/AvailabilityToggle";
import LocationTracker from "@/components/LocationTracker";
import PendingOfferBanner from "@/components/PendingOfferBanner";
import { DriverMenu } from "@/components/driver/DriverMenu";
import {
  EarlyMorningBanner, WeekAgendaBar, ScheduleTabs, addDays, fmtShort, toYmd,
} from "@/components/driver/ScheduleControls";
import {
  pushSupported, pushPermission, currentSubscription, subscribeToPush,
  registerServiceWorker, pushDismissed, dismissPushForNow,
  ensurePushKeyInSync,
} from "@/lib/pushClient";

const STATUS_LABEL = {
  assigned: { text: "Dodijeljeno", tone: "bg-zinc-800 text-zinc-300 border-zinc-700" },
  in_progress: { text: "U tijeku", tone: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/40" },
  completed: { text: "Završeno", tone: "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" },
};

function digits(p) { return (p || "").replace(/[^0-9]/g, ""); }

function RejectedReceiptsBanner() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    driverApi.get("/driver/fuel-receipts", { params: { limit: 30 } })
      .then(({ data }) => setCount((data?.rows || []).filter((r) => r.status === "rejected").length))
      .catch(() => {});
  }, []);
  if (!count) return null;
  return (
    <Link
      to="/driver/history"
      data-testid="rejected-receipts-banner"
      className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-3 text-xs text-red-300 hover:bg-red-500/10 transition"
    >
      <Receipt className="w-4 h-4 shrink-0" />
      <span className="flex-1">
        <strong className="text-red-200">{count} {count === 1 ? "račun je odbijen" : "računa je odbijeno"}.</strong>{" "}
        Pogledajte razlog i pošaljite novu fotografiju.
      </span>
      <ArrowRight className="w-4 h-4 shrink-0" />
    </Link>
  );
}

export default function DriverDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [rides, setRides] = useState([]);
  const [today, setToday] = useState(() => toYmd(new Date()));
  const [filter, setFilter] = useState({ type: "today" });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [pushState, setPushState] = useState("unknown"); // unknown|subscribed|denied|prompt|unsupported
  const [pushBusy, setPushBusy] = useState(false);
  const [settleRide, setSettleRide] = useState(null); // ride currently in settlement flow
  const [showReceiptUpload, setShowReceiptUpload] = useState(
    () => new URLSearchParams(window.location.search).get("upload") === "1"
      || !!sessionStorage.getItem("juke_receipt_pending"),
  );
  const [sbAgreement, setSbAgreement] = useState(null);
  const [sbBusy, setSbBusy] = useState(false);
  const [sbDismissed, setSbDismissed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meRes, ridesRes] = await Promise.all([
        driverApi.get("/driver/me"),
        driverApi.get("/driver/rides/schedule"),
      ]);
      setMe(meRes.data);
      setToday(ridesRes.data?.today || toYmd(new Date()));
      setRides(ridesRes.data?.rides || []);
      driverApi.get("/driver/self-billing/agreement")
        .then(({ data }) => setSbAgreement(data)).catch(() => {});
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("juke_driver_token");
        navigate("/driver");
      } else {
        toast.error("Učitavanje vožnji nije uspjelo.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  // Push notification setup — register SW on mount and detect current state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!pushSupported()) { setPushState("unsupported"); return; }
      await registerServiceWorker();
      const perm = pushPermission();
      if (perm === "denied") { if (!cancelled) setPushState("denied"); return; }
      // iter60 — silently re-align the subscription if the server VAPID
      // key rotated (prevents "applicationServerKey is not valid" from
      // ever surfacing after a backend key change).
      let sub = null;
      try { sub = await ensurePushKeyInSync(); } catch (_) { /* noop */ }
      if (!sub) sub = await currentSubscription();
      if (cancelled) return;
      setPushState(sub ? "subscribed" : (perm === "granted" ? "prompt" : "prompt"));
    })();
    return () => { cancelled = true; };
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeToPush();
      setPushState("subscribed");
      toast.success("Push obavijesti su uključene.");
    } catch (err) {
      const msg = err?.message || "Push obavijesti nije moguće uključiti.";
      toast.error(msg);
      if (msg.toLowerCase().includes("permission")) setPushState("denied");
    } finally {
      setPushBusy(false);
    }
  };

  const dismissPushBanner = () => {
    dismissPushForNow();
    setPushState("dismissed");
  };

  const logout = () => {
    localStorage.removeItem("juke_driver_token");
    navigate("/driver");
  };

  const openWaLink = (url) => {
    if (!url) return;
    // Open the pre-composed wa.me link so the driver taps "Send" once.
    // We use deep-link mode by default so the number stays active on the owner's phone.
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const startTrip = async (ride) => {
    setBusyId(ride.id);
    try {
      const { data } = await driverApi.post(`/driver/rides/${ride.id}/start`);
      toast.success("Vožnja započeta. Klijent je obaviješten.");
      // If we fell back to deep-link mode, open WhatsApp to let the driver tap Send once.
      if (data.whatsapp?.mode === "deeplink" && data.whatsapp?.url) {
        openWaLink(data.whatsapp.url);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vožnju nije moguće započeti.");
    } finally {
      setBusyId(null);
    }
  };

  const openSettlement = (ride) => setSettleRide(ride);
  const closeSettlement = () => setSettleRide(null);
  const onSettlementComplete = async () => {
    // Refresh list in the background; modal stays open on the receipt.
    await load();
  };

  const tomorrow = addDays(today, 1);
  const weekEnd = addDays(today, 6);
  const todayRides = rides.filter((r) => r.pickup_date === today);
  const activeCount = todayRides.filter((r) => r.trip_status !== "completed").length;
  const completedToday = todayRides.length - activeCount;
  const dayCounts = {};
  rides.forEach((r) => { dayCounts[r.pickup_date] = (dayCounts[r.pickup_date] || 0) + 1; });
  const visibleRides = rides.filter((r) => {
    if (filter.type === "today") return r.pickup_date === today;
    if (filter.type === "tomorrow") return r.pickup_date === tomorrow;
    if (filter.type === "week") return r.pickup_date >= today && r.pickup_date <= weekEnd;
    return r.pickup_date === filter.date;
  });
  const earlyRide = rides.find(
    (r) => r.pickup_date === tomorrow && (r.pickup_time || "") < "08:00" && r.trip_status !== "completed",
  );
  const emptyText = {
    today: "Za danas nema dodijeljenih vožnji.",
    tomorrow: "Za sutra nema dodijeljenih vožnji.",
    week: "U sljedećih 7 dana nema dodijeljenih vožnji.",
    day: `Za ${filter.date ? fmtShort(filter.date) : "odabrani dan"} nema dodijeljenih vožnji.`,
  }[filter.type];

  return (
    <div
      data-testid="driver-dashboard"
      className="min-h-screen bg-[#050505] text-zinc-100 pb-24"
    >
      <header className="border-b border-white/10 bg-[#080808] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 h-16 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">
              {me?.driver_type === "subcontractor" ? "Podizvođač" : "Vozač"}
            </p>
            <p data-testid="driver-name" className="text-sm text-white font-medium">
              {me?.name || "…"}
            </p>
          </div>
          <DriverMenu
            driverName={me?.name}
            roleLabel={me?.driver_type === "subcontractor" ? "Podizvođač" : "Vozač"}
            items={[
              // Dynamic menu — add new features by appending objects here.
              { section: "Navigacija", key: "earnings", label: "Zarada i Vožnje",
                sub: me?.driver_type === "subcontractor" ? "Moji obračuni (self-billing)" : "Moja zarada",
                icon: FileText, to: "/driver/obracuni", testId: "driver-statements-link" },
              { section: "Navigacija", key: "documents", label: "Moji Dokumenti",
                sub: "Dozvole, certifikati i ugovori", icon: FolderOpen,
                to: "/driver/documents", testId: "driver-documents-link" },
              { section: "Navigacija", key: "history", label: "Tjedna zarada i isplate",
                sub: "Povijest tjednih obračuna", icon: TrendingUp,
                to: "/driver/history", testId: "driver-history-link" },
              { section: "Navigacija", key: "benefits", label: "Pogodnosti za vozače",
                sub: "Partnerski popusti i benefiti", icon: Gift,
                to: "/driver/benefits", testId: "driver-benefits-link" },
              { section: "Radnje", key: "receipt", label: "Unos računa/troška",
                sub: "Skeniraj ili učitaj račun", icon: Receipt,
                onClick: () => setShowReceiptUpload(true), testId: "driver-receipt-open" },
              { section: "Radnje", key: "refresh", label: "Osvježi",
                sub: "Ponovno učitaj podatke", icon: RefreshCw,
                onClick: load, testId: "driver-refresh-btn" },
              { section: "Račun", key: "profile", label: "Moj profil",
                sub: "Registrirani podaci i vozilo", icon: UserRound,
                to: "/driver/profile", testId: "driver-profile-link" },
              { section: "Račun", key: "logout", label: "Odjavi se",
                icon: LogOut, onClick: logout, danger: true, testId: "driver-logout-btn" },
            ]}
          />
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 pt-6">
        {/* Push notifications banner */}
        {pushState === "prompt" && !pushDismissed() && (
          <div
            data-testid="driver-push-banner"
            className="mb-4 rounded-2xl border border-[#C5A059]/40 bg-[#C5A059]/5 p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 shrink-0 rounded-full bg-[#C5A059]/15 border border-[#C5A059]/30 flex items-center justify-center">
              <Bell className="w-5 h-5 text-[#C5A059]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium">Uključite obavijesti o vožnjama</p>
              <p className="text-xs text-zinc-400">Telefon će vas obavijestiti čim dobijete novu vožnju.</p>
            </div>
            <button
              onClick={enablePush}
              disabled={pushBusy}
              data-testid="driver-push-enable-btn"
              className="h-10 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black text-xs font-semibold uppercase tracking-[0.15em] transition-colors"
            >
              {pushBusy ? "…" : "Uključi"}
            </button>
            <button
              onClick={dismissPushBanner}
              data-testid="driver-push-dismiss-btn"
              aria-label="Dismiss"
              className="p-2 text-zinc-500 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {pushState === "denied" && (
          <div
            data-testid="driver-push-denied"
            className="mb-4 rounded-2xl border border-red-500/30 bg-red-500/5 p-3 flex items-center gap-3 text-xs text-red-300"
          >
            <BellOff className="w-4 h-4 shrink-0" />
            <span>
              Obavijesti su blokirane. Otvorite postavke preglednika za ovu stranicu i postavite
              <strong className="text-red-200"> Obavijesti → Dopusti</strong> kako biste primali obavijesti o vožnjama.
            </span>
          </div>
        )}

        <PendingOfferBanner />
        <EarlyMorningBanner ride={earlyRide} />
        <RejectedReceiptsBanner />
        <AvailabilityToggle />
        <LocationTracker enabled />

        {/* Summary strip */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Danas · aktivno</p>
            <p data-testid="driver-active-count" className="text-3xl font-display text-white mt-1">{activeCount}</p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4">
            <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Završeno danas</p>
            <p data-testid="driver-completed-count" className="text-3xl font-display text-[#C5A059] mt-1">{completedToday}</p>
          </div>
        </div>

        {/* Weekly agenda + upcoming filters */}
        <WeekAgendaBar
          today={today}
          counts={dayCounts}
          selectedDate={filter.type === "day" ? filter.date : (filter.type === "today" ? today : filter.type === "tomorrow" ? tomorrow : null)}
          onSelectDay={(d) => setFilter(d === today ? { type: "today" } : d === tomorrow ? { type: "tomorrow" } : { type: "day", date: d })}
        />
        <ScheduleTabs filter={filter} onChange={setFilter} />

        {visibleRides.length === 0 && !loading && (
          <div
            data-testid="driver-no-rides"
            className="rounded-2xl border border-dashed border-white/10 p-10 text-center"
          >
            <p className="text-zinc-400">{emptyText}</p>
            <p className="text-xs text-zinc-600 mt-2">Slobodni ste — ured će vas obavijestiti kada stigne nova vožnja.</p>
          </div>
        )}

        <div className="space-y-4">
          {visibleRides.map((r) => {
            const s = STATUS_LABEL[r.trip_status] || STATUS_LABEL.assigned;
            const phoneNum = digits(r.phone);
            const isB2B = !!r.partner_id;
            const b2bCollect = r.billing_type === "pay_in_vehicle";
            const waMsg = encodeURIComponent(
              `Hi ${(r.name || "").split(" ")[0] || "there"}, this is your driver from Juke Transfers regarding your ${r.pickup_date} ${r.pickup_time} pickup.`
            );
            return (
              <article
                key={r.id}
                data-testid={`ride-card-${r.id}`}
                className="rounded-2xl border border-white/10 bg-[#0d0d0d] overflow-hidden"
              >
                <div className="p-5 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <Clock className="w-4 h-4 text-[#C5A059] shrink-0" />
                      <span className="text-2xl font-display text-white">{r.pickup_time}</span>
                      {r.pickup_date !== today && (
                        <span
                          data-testid={`ride-date-chip-${r.id}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-zinc-300 border border-white/15 bg-white/5 rounded-full px-2 py-0.5"
                        >
                          <CalendarDays className="w-3 h-3" /> {fmtShort(r.pickup_date)}
                        </span>
                      )}
                      {r.flight_number && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-2 py-0.5 ml-1">
                          <PlaneTakeoff className="w-3 h-3" /> {r.flight_number}
                        </span>
                      )}
                    </div>
                    <p className="text-white font-medium truncate">{r.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">
                      {r.ref || "—"} · {r.passengers} putnika · {r.luggage} torbi{r.vehicle_type ? ` · ${r.vehicle_type}` : ""}
                    </p>
                    {isB2B && (
                      <span
                        data-testid={`ride-b2b-badge-${r.id}`}
                        className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-sky-300 border border-sky-400/40 bg-sky-500/10 rounded-full px-2.5 py-0.5"
                      >
                        <Briefcase className="w-3 h-3" /> B2B Partner · {r.partner_company_name || "—"}
                      </span>
                    )}
                  </div>
                  <span className={`shrink-0 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${s.tone}`}>
                    {s.text}
                  </span>
                </div>

                <div className="px-5 pb-4 space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                    <span className="text-zinc-200">{r.pickup_location}</span>
                  </div>
                  {r.dropoff_location && (
                    <div className="flex items-start gap-2">
                      <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0 mt-0.5" />
                      <span className="text-zinc-300">{r.dropoff_location}</span>
                    </div>
                  )}
                  {r.notes && (
                    <p className="text-xs text-zinc-500 italic pt-1 border-t border-white/5 mt-2">
                      Napomena: {r.notes}
                    </p>
                  )}
                </div>

                {/* B2B payment instructions — high-visibility box */}
                {isB2B && (
                  <div className="px-5 pb-4">
                    {b2bCollect ? (
                      <div
                        data-testid={`ride-b2b-collect-${r.id}`}
                        className="rounded-xl border-2 border-emerald-500/60 bg-emerald-500/10 p-4"
                      >
                        <p className="text-emerald-300 font-bold text-base leading-snug">
                          NAPLATI OD GOSTA (€{Number(r.quote_amount || 0).toFixed(2)}) — Gotovina ili SoftPOS
                        </p>
                        <p className="text-xs text-emerald-200/70 mt-1">
                          Vožnja nije na mjesečni račun, naplata na licu mjesta.
                        </p>
                      </div>
                    ) : (
                      <div
                        data-testid={`ride-b2b-noinvoice-${r.id}`}
                        className="rounded-xl border-2 border-sky-400/60 bg-sky-500/10 p-4"
                      >
                        <p className="text-sky-300 font-bold text-base leading-snug">
                          NE NAPLAĆUJ OD GOSTA (€0.00) — Mjesečni B2B Račun
                        </p>
                        <p className="text-xs text-sky-200/70 mt-1">
                          Vožnja se obračunava direktno tvrtki na kraju mjeseca.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Client contact row */}
                <div className="px-5 pb-4 flex items-center gap-2">
                  <a
                    href={`tel:${phoneNum}`}
                    data-testid={`ride-call-${r.id}`}
                    className="flex-1 h-12 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 flex items-center justify-center gap-2 text-sm text-zinc-200 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    Nazovi {r.phone}
                  </a>
                  <a
                    href={`https://wa.me/${phoneNum}?text=${waMsg}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`ride-wa-${r.id}`}
                    className="h-12 w-12 rounded-xl bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 transition-colors"
                    aria-label="WhatsApp client"
                  >
                    <MessageCircle className="w-5 h-5" />
                  </a>
                </div>

                {/* Navigation row — pickup + drop-off deep links (Google Maps / Waze) */}
                {r.trip_status !== "completed" && (r.pickup_location || r.dropoff_location) && (
                  <div className="px-5 pb-4 grid grid-cols-2 gap-2">
                    {r.pickup_location && r.trip_status === "assigned" && (
                      <NavButton
                        testid={`ride-nav-pickup-${r.id}`}
                        label="Do polazišta"
                        address={r.pickup_location}
                        variant="pickup"
                      />
                    )}
                    {r.dropoff_location && (
                      <NavButton
                        testid={`ride-nav-dropoff-${r.id}`}
                        label={r.trip_status === "in_progress" ? "Navigacija" : "Do odredišta"}
                        address={r.dropoff_location}
                        variant="dropoff"
                        wide={r.trip_status === "in_progress" || !r.pickup_location}
                      />
                    )}
                  </div>
                )}

                {/* Action buttons */}
                <div className="px-5 pb-5">
                  {r.trip_status === "assigned" && (
                    <button
                      onClick={() => startTrip(r)}
                      disabled={busyId === r.id}
                      data-testid={`ride-start-btn-${r.id}`}
                      className="w-full h-14 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Play className="w-5 h-5" fill="currentColor" />
                      {busyId === r.id ? "Pokretanje…" : "Započni vožnju"}
                    </button>
                  )}
                  {r.trip_status === "in_progress" && (
                    <button
                      onClick={() => openSettlement(r)}
                      disabled={busyId === r.id}
                      data-testid={`ride-settle-btn-${r.id}`}
                      className="w-full h-14 rounded-xl bg-emerald-500 hover:bg-emerald-400 disabled:opacity-60 text-black font-semibold text-lg flex items-center justify-center gap-2 transition-colors"
                    >
                      <Wallet className="w-5 h-5" />
                      Naplata i završetak
                    </button>
                  )}
                  {r.trip_status === "completed" && (
                    <div className="w-full h-14 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-medium flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Završeno
                    </div>
                  )}
                  {r.invoice_url && (
                    <a
                      href={r.invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`ride-invoice-${r.id}`}
                      className="mt-3 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-[#C5A059] transition-colors"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Pregledaj račun
                    </a>
                  )}
                </div>

                {(r.passengers > 4 || r.luggage > 3) && (
                  <div className="px-5 py-2 bg-amber-500/10 border-t border-amber-500/20 flex items-center gap-2 text-xs text-amber-300">
                    <Users className="w-3.5 h-3.5" />
                    Veća grupa — provjerite kapacitet
                    <Briefcase className="w-3.5 h-3.5 ml-2" />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
      <InstallPrompt />
      {sbAgreement?.required && !sbAgreement?.agreed && !sbDismissed && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto">
          <div data-testid="self-billing-modal" className="w-full max-w-lg my-8 bg-[#0d0d0d] border border-[#C5A059]/30 rounded-2xl p-6">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C5A059] mb-1">Samoizdavanje računa</p>
            <h3 className="text-lg text-white font-light mb-4">Ugovor o samoizdavanju računa</h3>
            <div className="max-h-64 overflow-y-auto rounded-xl bg-black/40 border border-white/10 p-4 text-xs text-zinc-300 whitespace-pre-line leading-relaxed mb-4">
              {sbAgreement.text}
            </div>
            <p className="text-xs text-zinc-500 mb-4">
              Bez prihvaćanja ugovora nije moguće generirati mjesečne obračune (isplate).
            </p>
            <div className="flex gap-2">
              <button
                data-testid="self-billing-accept"
                disabled={sbBusy}
                onClick={async () => {
                  setSbBusy(true);
                  try {
                    await driverApi.post("/driver/self-billing/agree");
                    setSbAgreement({ ...sbAgreement, agreed: true });
                    toast.success("Ugovor prihvaćen");
                  } catch { toast.error("Nije uspjelo, pokušajte ponovno."); }
                  finally { setSbBusy(false); }
                }}
                className="flex-1 h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-sm uppercase tracking-[0.15em] font-semibold disabled:opacity-50"
              >
                Prihvaćam ugovor
              </button>
              <button
                data-testid="self-billing-later"
                onClick={() => setSbDismissed(true)}
                className="h-12 px-5 rounded-full border border-white/15 text-zinc-400 text-sm"
              >
                Kasnije
              </button>
            </div>
          </div>
        </div>
      )}

      {settleRide && (
        <SettlementModal
          ride={settleRide}
          onClose={closeSettlement}
          onComplete={onSettlementComplete}
        />
      )}
      {showReceiptUpload && (
        <DriverReceiptUpload
          onClose={() => setShowReceiptUpload(false)}
        />
      )}
    </div>
  );
}


/**
 * NavButton — opens Google Maps or Waze with the destination pre-filled.
 * Tap opens a small chooser sheet so the driver keeps their preferred
 * nav app; both options are one tap away.
 */
function NavButton({ testid, label, address, variant = "dropoff", wide = false }) {
  const [open, setOpen] = useState(false);
  const enc = encodeURIComponent(address);
  const gmapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${enc}&travelmode=driving`;
  const wazeUrl = `https://waze.com/ul?q=${enc}&navigate=yes`;
  const isPickup = variant === "pickup";
  const base = isPickup
    ? "bg-white/5 hover:bg-white/10 border-white/10 text-emerald-300"
    : "bg-[#C5A059]/10 hover:bg-[#C5A059]/20 border-[#C5A059]/30 text-[#C5A059]";
  return (
    <div className={`relative ${wide ? "col-span-2" : ""}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={testid}
        className={`w-full h-12 rounded-xl border flex items-center justify-center gap-2 text-sm font-medium transition-colors ${base}`}
        aria-label={`${label} navigation`}
      >
        <Navigation2 className="w-4 h-4" />
        {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div
            className="absolute z-50 top-full mt-1 left-0 right-0 rounded-xl bg-zinc-900 border border-white/10 shadow-2xl overflow-hidden"
            data-testid={`${testid}-sheet`}
          >
            <a
              href={gmapsUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-gmaps`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm text-white transition-colors"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#4285F4" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C7.58 2 4 5.58 4 10c0 7 8 12 8 12s8-5 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 100-6 3 3 0 000 6z"/>
              </svg>
              Google Maps
            </a>
            <a
              href={wazeUrl}
              target="_blank" rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid={`${testid}-waze`}
              className="flex items-center gap-3 px-4 py-3 hover:bg-white/5 text-sm text-white transition-colors border-t border-white/5"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#33CCFF" xmlns="http://www.w3.org/2000/svg">
                <path d="M20.54 6.63c1.4 3.03.66 6.51-1.94 8.79-1.85 1.62-4.32 2.4-6.85 2.09-.7-.09-1.35.32-1.52.99-.35 1.35-1.66 2.34-3.19 2.34-1.82 0-3.3-1.43-3.3-3.2 0-.28.04-.55.11-.81C1.7 15.3.5 12.79.5 10c0-4.42 4.03-8 9-8 4.75 0 9.35 2.62 11.04 4.63zM7 11.5a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2zm-1.5 3c-.83 0-1.5-.67-1.5-1.5h3c0 .83-.67 1.5-1.5 1.5z"/>
              </svg>
              Waze
            </a>
          </div>
        </>
      )}
    </div>
  );
}
